/**
 * EXP-1.3 · Point back.
 *
 * When a library template is applied, each `checklist_items` row it creates
 * records which library item it came from, so the field can resolve that
 * item's authored example photos rather than owning a copy of them.
 *
 * The link did not exist before this story. `cloned_from_checklist_item_id`
 * points at another `checklist_items` row, which is a relational clone, and
 * `checklist_template_applications` holds zero rows and is per template
 * rather than per item. So the apply path in `ChecklistsTab.tsx` is the one
 * place this is populated, and this module is the one rule it follows.
 *
 * ⚠️ BOTH COLUMNS OR NEITHER. A template id with no item id is worse than no
 * link at all: `checklist_item_examples_for()` would look for library rows
 * keyed on a NULL item id, match none, and fall through to the item's own
 * examples anyway. So it buys nothing, and it leaves a half-populated link
 * for the next reader to puzzle over.
 *
 * Measured live 2026-09-10: every library item carries an `id`, 1043 of 1043.
 * The null branch is defensive, not expected.
 */

export interface LibraryItemLink {
  source_library_template_id: string | null;
  source_library_item_id: string | null;
}

const NO_LINK: LibraryItemLink = {
  source_library_template_id: null,
  source_library_item_id: null,
};

/**
 * Derive the two link columns for one applied item.
 *
 * @param libraryTemplateId the library template the items were loaded from,
 *   or null when they did not come from one
 * @param libraryItemId the `id` key of the element of
 *   `configuration.items[]` this item was built from. Numbers are accepted
 *   and stringified: items imported from a pipeline carry the numeric
 *   `checklist_items` id, while template-mode authoring generates strings
 *   like `item-1757...`.
 */
export function libraryItemLink(
  libraryTemplateId: string | null | undefined,
  libraryItemId: unknown,
): LibraryItemLink {
  if (!libraryTemplateId) return NO_LINK;
  if (libraryItemId === null || libraryItemId === undefined) return NO_LINK;

  const itemId = String(libraryItemId).trim();
  // An empty or whitespace id addresses nothing, and storing it would look
  // like a link while resolving to none.
  if (itemId === '') return NO_LINK;

  return {
    source_library_template_id: libraryTemplateId,
    source_library_item_id: itemId,
  };
}
